import { concat } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismBanner,
  PrismButton,
  PrismModal,
} from '@mylighthouse/prism-ember';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { not, or } from 'ember-truth-helpers';
import DateRangePicker from 'frontend-payments/components/date-range-picker';
import type { Account } from 'frontend-payments/models';
import { getApiBaseUrl } from 'frontend-payments/utils/api-config';
import { downloadPdf } from 'frontend-payments/utils/download-pdf';

import styles from './modal-statement-of-account.module.css';

interface PaymentModalStatementOfAccountSignature {
  Args: {
    account: Account | null;
    onCloseModal: () => void;
  };
}

export default class PaymentModalStatementOfAccount extends Component<PaymentModalStatementOfAccountSignature> {
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked endDate: Dayjs | undefined = undefined;
  @tracked startDate: Dayjs | undefined = undefined;

  downloadStatementOfAccountTask = task(async (): Promise<void> => {
    if (
      !this.isFormValid ||
      !this.args.account ||
      !this.startDate ||
      !this.endDate
    ) {
      return;
    }

    try {
      const startDateFormatted = this.startDate.format('YYYY-MM-DD');
      const endDateFormatted = this.endDate.format('YYYY-MM-DD');
      const accountNumber = this.args.account.accountNumber;

      const url = `${getApiBaseUrl()}/accounts/${accountNumber}/transactions/statement-of-accounts?start_date=${startDateFormatted}&end_date=${endDateFormatted}`;
      const filename = `statement-of-account-${accountNumber}-${startDateFormatted}-to-${endDateFormatted}.pdf`;

      await downloadPdf(url, filename);

      this.toast.success(
        this.intl.t('payments.statement_of_account.download_success_message'),
        {
          title: this.intl.t(
            'payments.statement_of_account.download_success_title',
          ),
          showIcon: true,
        },
      );

      this.usageAnalytics.track('SOA Downloaded', {
        eventTrigger: 'header',
      });

      // Close modal after successful download
      this.args.onCloseModal();
    } catch (error) {
      this.errorTracking.captureException(error);
      this.toast.error(
        this.intl.t('payments.statement_of_account.download_error_message'),
        {
          title: this.intl.t(
            'payments.statement_of_account.download_error_title',
          ),
          timeout: TOAST_TIMEOUT_LONG,
          showIcon: true,
        },
      );
    }
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isFormValid() {
    return (
      this.startDate &&
      this.endDate &&
      this.startDate.isBefore(this.endDate) &&
      !this.endDate.isAfter(dayjs())
    );
  }

  @action
  onEndDateChange(date: Dayjs | null): void {
    this.endDate = date || undefined;
  }

  @action
  onStartDateChange(date: Dayjs | null): void {
    this.startDate = date || undefined;
  }

  <template>
    <PrismModal
      @onClose={{@onCloseModal}}
      @title={{t "payments.statement_of_account.title"}}
    >
      <:default>
        <div class="modal--small modal--padded">
          <p class="text-300--bold mb-300">
            {{t "payments.statement_of_account.description"}}
          </p>
          {{! A custom date range picker component is used here as the DatepickerFormField is only available in Lumen, this can be replaced when we would have a new Daterange component }}
          <DateRangePicker
            @endDate={{this.endDate}}
            @onEndDateChange={{this.onEndDateChange}}
            @onStartDateChange={{this.onStartDateChange}}
            @startDate={{this.startDate}}
          />
          {{#if this.downloadStatementOfAccountTask.isRunning}}
            <PrismBanner
              @bannerMessage={{t
                "payments.statement_of_account.download_in_progress_message"
              }}
              @icon="circle-information-outline"
            />
          {{/if}}
        </div>
      </:default>
      <:footer>
        <div class={{concat styles.footer " " "flex justify-end"}}>
          <PrismButton
            @buttonType="secondary"
            @isDisabled={{this.downloadStatementOfAccountTask.isRunning}}
            @label={{t "general.cancel"}}
            @onClick={{@onCloseModal}}
          />
          <PrismButton
            @isDisabled={{or
              (not this.isFormValid)
              this.downloadStatementOfAccountTask.isRunning
            }}
            @isLoading={{this.downloadStatementOfAccountTask.isRunning}}
            @label={{t
              "payments.statement_of_account.download_statement_of_account"
            }}
            @onClick={{this.downloadStatementOfAccountTask.perform}}
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
